import { AccountsHeader } from "../components/accounts_header";
import { AccountsToolbar } from "../components/accounts_toolbar";
import { AccountsGrid } from "../components/accounts_grid";
import { UserPlanOverview } from "../components/user_plan_overview";
import type { UserProfile } from "../models/UserProfile";
import GlassBackground from "@/shared/components/glassBackground";
import { AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { useAccountManager } from "../hooks/useAccountManager";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { confirmDialog } from "@/shared/components/dialog";

export default function AccountManagerContent() {
  const [accounts, setAccounts] = useState<UserProfile[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<UserProfile>();
  const [term, setTerm] = useState("");
  const debouncedTerm = useDebounce(term);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sendingMailId, setSendingMailId] = useState<string | null>(null);
  const { getPage, deleteAccount, resendConfirmation, sendPasswordReset, loading, error } =
    useAccountManager();

  useEffect(() => {
    let ignore = false;
    const fetchAccounts = async () => {
      const page = await getPage({
        pageNumber: 1,
        pageSize: 100,
        filter: debouncedTerm
          ? [
            {
              propertyName: "Name",
              action: "contains",
              value: debouncedTerm,
            },
          ]
          : null,
      });
      if (!ignore && page) {
        setAccounts(page.items);
      }
    };
    fetchAccounts();
    return () => { ignore = true; };
  }, [getPage, debouncedTerm]);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    const confirmDelete = await confirmDialog({
      title: "Eliminar Cuentas",
      message: `¿Estás seguro de que deseas eliminar ${selectedIds.length} cuentas? Esta acción no se puede deshacer.`,
      confirmLabel: "Sí, eliminar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmDelete) return;

    try {
      await Promise.all(selectedIds.map((id) => deleteAccount(id)));
      setAccounts((prev) => prev.filter((acc) => !selectedIds.includes(acc.id)));
      setSelectedIds([]);
    } catch {
      // error toast is handled by useAsync
    }
  };

  const handleResendConfirmation = async (account: UserProfile) => {
    setSendingMailId(account.id);
    try {
      await resendConfirmation(account.id);
    } catch {
      // el toast de error lo maneja useAsync
    } finally {
      setSendingMailId(null);
    }
  };

  const handleSendPasswordReset = async (account: UserProfile) => {
    const confirmed = await confirmDialog({
      title: "Enviar cambio de contraseña",
      message: `Se enviará a ${account.email} un enlace para elegir una nueva contraseña.`,
      confirmLabel: "Enviar",
      cancelLabel: "Cancelar",
    });
    if (!confirmed) return;

    setSendingMailId(account.id);
    try {
      await sendPasswordReset(account.id);
    } catch {
      // el toast de error lo maneja useAsync
    } finally {
      setSendingMailId(null);
    }
  };

  const handleViewPlan = (account: UserProfile) => {
    setSelectedAccount(account);
  };

  return (
    <GlassBackground>
      <div className="flex-1 overflow-auto p-10">
        <AccountsHeader />
        <AccountsToolbar
          selectedIds={selectedIds}
          handleDeleteSelected={handleDeleteSelected}
          searchTerm={term}
          setSearchTerm={setTerm}
        />
        <AccountsGrid
          loading={loading}
          error={error}
          accounts={accounts}
          selectedIds={selectedIds}
          toggleSelection={toggleSelection}
          onViewPlan={handleViewPlan}
          sendingMailId={sendingMailId}
          onResendConfirmation={handleResendConfirmation}
          onSendPasswordReset={handleSendPasswordReset}
        />
      </div>

      <AnimatePresence>
        {selectedAccount && (
          <UserPlanOverview
            user={selectedAccount}
            onClose={() => setSelectedAccount(undefined)}
          />
        )}
      </AnimatePresence>
    </GlassBackground>
  );
}

